import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Global, css } from '@emotion/core'
import { isNil, isEmpty } from 'ramda'
import { useSelector } from 'hooks/redux'
import DividerPipe from 'components/DividerPipe'
import { getVehicleSubType } from 'selectors/truck'
import strings from 'utils/strings'
import InputSwitchList from '../InputSwitchList'
import withStyle, {
  StyledModalContent,
  SideMenu,
  FeatureInputSwitchSelectionContainer,
  InputCardContentSwitchDetailsContainer,
  SwitchDescriptionSpan,
  PanelDescription,
  StyledInputSwitchSelectionModal,
  SelectedSwitchDescriptionSpan,
  FeatureDeailsContainer,
  AlertIconCheckMark,
  CloseButton,
  CloseIcon,
  HeaderTitle,
  HeaderLinkIcon,
  SignalNameHeader,
  PartNumberText,
  WarningAlertIcon,
} from './FeatureInputSwitchSelectionModal.style'

// PBI 147363 - Documentation 4: Global styles for switch selection modal
const modalStyles = css`
  .chec-error-modal .ant-modal-close {
    display: none !important;
  }

  .chec-error-modal .ant-modal-title {
    width: 100%;
  }

  .chec-error-modal .ant-modal-footer {
    display: flex;
    height: 72px;
    padding: 0 25px;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    align-self: stretch;
    border-radius: 0 0 3px 3px;
    border-top: 1px solid #e3e5e8;
    background: #fff;
    box-sizing: border-box;

    .ant-btn + .ant-btn {
      margin-left: 0;
    }
  }

  .ant-modal-mask {
    background: rgba(7, 7, 15, 0.7) !important;
  }
`

const stripSignalType = (name) =>
  name ? name.replace(/\s*\([^)]*\)\s*$/, '') : ''

// PBI 147363 - Documentation 4: Updated tile header to show signal name prominently
const InputCardHeader = ({
  featurePartNumber,
  description,
  moduleName,
  subType,
}) => (
  <>
    <SignalNameHeader>{stripSignalType(description)}</SignalNameHeader>
    <PartNumberText>
      {featurePartNumber} <DividerPipe /> {subType} <DividerPipe /> {moduleName}
    </PartNumberText>
  </>
)

InputCardHeader.propTypes = {
  featurePartNumber: PropTypes.string,
  description: PropTypes.string,
  moduleName: PropTypes.string,
  subType: PropTypes.string,
}

const InputCardContent = ({ selectedSwitch, switches, ...props }) => {
  if (isNil(selectedSwitch) || isEmpty(selectedSwitch)) {
    return (
      <InputCardContentSwitchDetailsContainer>
        <WarningAlertIcon />
        <SwitchDescriptionSpan>
          {strings.signal_requires_switch}
        </SwitchDescriptionSpan>
      </InputCardContentSwitchDetailsContainer>
    )
  }
  const selectedSwitcheDetails = switches.find(
    (x) => x.panelNumber === selectedSwitch
  )
  return (
    <PanelDescription>
      <AlertIconCheckMark icon="check-mark-only" fill="alertSuccessBgColor" />
      <SelectedSwitchDescriptionSpan type="h4">
        {stripSignalType(selectedSwitcheDetails.panelDescription)}
      </SelectedSwitchDescriptionSpan>
    </PanelDescription>
  )
}

InputCardContent.propTypes = {
  selectedSwitch: PropTypes.string,
  moduleName: PropTypes.string,
  switches: PropTypes.array,
}

const FeatureInputSwitchSelectionModal = (props) => {
  const {
    isOpen,
    onClickSelect,
    onClickCancel,
    featureInput,
    serialNumber,
    isSwitchesMarkedForAddition,
    addedSwitches,
    selectedSearchFeatures,
  } = props

  const [selectedInput, setSelectedInput] = useState(0)
  const [selectedInputDetails, setSelectedInputDetails] = useState(
    featureInput[0]
  )

  useEffect(() => {
    if (isSwitchesMarkedForAddition) {
      setSelectedSwitches(addedSwitches)
    } else {
      const preSelectedSwitches = featureInput.reduce((acc, current) => {
        const { switches, id } = current
        const onVehicleSwitches = switches.filter(
          ({ isSwitchOnVehicle }) => isSwitchOnVehicle
        )
        // Only preselect when exactly one on-vehicle match exists.
        // Multiple matches require explicit user choice.
        if (onVehicleSwitches.length === 1) {
          const { panelNumber } = onVehicleSwitches[0]
          return { ...acc, [id]: panelNumber }
        }
        return acc
      }, {})
      setSelectedSwitches(preSelectedSwitches)
    }
  }, [addedSwitches, featureInput, isSwitchesMarkedForAddition])
  const [selectedSwitches, setSelectedSwitches] = useState({})

  const subType = useSelector(getVehicleSubType, { serialNumber })

  const handleCommitSelection = () => {
    onClickSelect(selectedSwitches)
  }

  const handleCardOnClick = (index) => {
    setSelectedInput(index)
    setSelectedInputDetails(featureInput[index])
  }

  const isConfirmEnabled = () => {
    const includedFeatures = Object.keys(selectedSwitches)

    return featureInput.every(({ id }) => includedFeatures.includes(id))
  }

  const handleSwitchSelection = useCallback(
    (switchNumber) => {
      const inputId = selectedInputDetails.id
      const newSelectedSwitch = { ...selectedSwitches, [inputId]: switchNumber }
      setSelectedSwitches(newSelectedSwitch)
    },
    [selectedInputDetails.id, selectedSwitches]
  )

  const cards = featureInput.map((card, index) => {
    // Default to an empty object rather than destructuring the lookup directly.
    // The rows can span several additions, so a caller that passes a narrower
    // feature list would otherwise take the whole modal down with a TypeError and
    // strand the user on a mapping they cannot complete. Callers are expected to
    // pass metadata covering every row (see getFeaturesForMappingRows); this only
    // makes the failure mode a card with blank metadata instead of a crash.
    const { moduleName, partNumber: partNum } =
      selectedSearchFeatures.find((feature) => feature.id === card.featureId) ||
      {}

    return {
      key: `card-${card.id}`,
      header: (
        <InputCardHeader
          {...card}
          featurePartNumber={partNum}
          moduleName={moduleName}
          subType={subType}
        />
      ),
      content: (
        <InputCardContent
          {...card}
          selectedSwitch={selectedSwitches[card.id]}
        />
      ),
      onClick: handleCardOnClick,
      selected: selectedInput === index,
    }
  })

  return (
    <>
      <Global styles={modalStyles} />
      <StyledInputSwitchSelectionModal
        header={
          <FeatureDeailsContainer>
            <HeaderLinkIcon />
            <HeaderTitle>
              {strings.select_switches_to_use_with_new_features(serialNumber)}
            </HeaderTitle>
            <CloseButton onClick={onClickCancel} aria-label="Close modal">
              <CloseIcon icon="cancel" fill="darkSlate" />
            </CloseButton>
          </FeatureDeailsContainer>
        }
        isOpen={isOpen}
        className="chec-error-modal"
        size="extraLarge"
        customButtons={[
          {
            text: strings.cancel,
            onClick: onClickCancel,
            type: 'secondary',
          },
          {
            text: strings.confirm,
            onClick: handleCommitSelection,
            disabled: !isConfirmEnabled(),
            type: 'primary',
          },
        ]}
      >
        <StyledModalContent>
          <FeatureInputSwitchSelectionContainer>
            <InputSwitchList
              handleSwitchSelection={handleSwitchSelection}
              selectedInputDetails={selectedInputDetails}
              selectedSwitches={selectedSwitches}
            />
            <SideMenu cards={cards} />
          </FeatureInputSwitchSelectionContainer>
        </StyledModalContent>
      </StyledInputSwitchSelectionModal>
    </>
  )
}

FeatureInputSwitchSelectionModal.propTypes = {
  isOpen: PropTypes.bool,
  serialNumber: PropTypes.string,
  onClickCancel: PropTypes.func,
  onClickSelect: PropTypes.func,
  featureInput: PropTypes.array,
  selectedFeature: PropTypes.object,
  isSwitchesMarkedForAddition: PropTypes.bool,
  addedSwitches: PropTypes.object,
  selectedSearchFeatures: PropTypes.array,
}

export default withStyle(FeatureInputSwitchSelectionModal)
